import { useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { createWarehouse } from '../../../api/warehouse';
import type { Warehouse } from '../../../types/shared';

interface WarehouseCreationModalProps {
  onClose: () => void;
  onSuccess: (warehouse: Warehouse) => void;
}

const WarehouseCreationModal = ({ onClose, onSuccess }: WarehouseCreationModalProps) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Warehouse name is required');
      return;
    }

    setLoading(true);
    try {
      const warehouse = await createWarehouse({
        name: formData.name,
        code: formData.code || undefined,
        description: formData.description || undefined,
      });
      onSuccess(warehouse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create warehouse');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg max-w-sm w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-bark-900 dark:text-stone-100">Create Warehouse</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle size={16} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1">
              Warehouse Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., NYC Warehouse"
              disabled={loading}
              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1">
              Code
            </label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="e.g., NYC-01"
              disabled={loading}
              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 disabled:opacity-50"
            />
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-1">Optional unique identifier</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Notes about this warehouse..."
              disabled={loading}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 disabled:opacity-50 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-stone-50 dark:hover:bg-[#242d28] transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-moss-600 text-white rounded-lg hover:bg-moss-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WarehouseCreationModal;
